// Google Analytics 4 — consent-gated ("manual tag blocking").
//
// The gtag.js script is NOT added to the page until the user explicitly grants
// consent (see cookie_consent_banner.jsx), so no analytics cookies (_ga, _gid,
// _ga_*) are ever set beforehand. This is the ePrivacy/GDPR-compliant behaviour.
//
// The measurement ID is read from the REACT_APP_GA_MEASUREMENT_ID build env var.
// Until a real ID is supplied it stays as the placeholder below and every
// function here is a no-op — nothing loads, nothing tracks.
import { getConsent, CONSENT } from './consent'

// TODO: replace with the real GA4 Measurement ID (or set REACT_APP_GA_MEASUREMENT_ID).
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX'

// A real ID looks like "G-ABC1234567"; the placeholder must not activate GA.
export const isAnalyticsConfigured = () =>
  /^G-[A-Z0-9]+$/.test(MEASUREMENT_ID) && MEASUREMENT_ID !== 'G-XXXXXXXXXX'

let initialized = false

// Injects gtag.js and boots GA. Idempotent, and a no-op until a real ID exists.
export const enableAnalytics = () => {
  if (initialized || !isAnalyticsConfigured() || typeof document === 'undefined') return
  initialized = true

  // Clear any prior opt-out flag from a previous "reject" in this session.
  window[`ga-disable-${MEASUREMENT_ID}`] = false

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() { window.dataLayer.push(arguments) }
  window.gtag('js', new Date())
  // We are an SPA, so we send page views ourselves via trackPageview().
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false })

  // Record the current page now that GA is live (the initial page view).
  trackPageview(window.location.pathname + window.location.search)
}

// Fires a single SPA page view. No-op until analytics is enabled.
export const trackPageview = (page) => {
  if (!initialized || typeof window.gtag !== 'function') return
  window.gtag('event', 'page_view', { page_path: page, page_location: window.location.href })
}

// Stops all further tracking and clears GA cookies (consent withdrawn).
export const disableAnalytics = () => {
  initialized = false
  if (typeof window !== 'undefined') {
    // Honoured by gtag.js — blocks any in-flight/queued hits for this ID.
    window[`ga-disable-${MEASUREMENT_ID}`] = true
  }
  clearGaCookies()
}

const clearGaCookies = () => {
  if (typeof document === 'undefined') return
  const host = window.location.hostname
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim()
    if (name === '_ga' || name.startsWith('_ga_') || name === '_gid' || name === '_gat') {
      // Expire on the current host and on the registrable domain (leading-dot form).
      document.cookie = `${name}=; ${expire}`
      document.cookie = `${name}=; ${expire}; domain=${host}`
      document.cookie = `${name}=; ${expire}; domain=.${host}`
    }
  })
}

// On load, honour a decision the user already made — without re-prompting.
if (getConsent() === CONSENT.GRANTED) enableAnalytics()
